// Simple base class for file uploads (no form submission)
import {
    FileUploadUtility,
    ISignedURLResponse,
    IUploadInfo
} from './file-upload-utility';
import { getQueryParam } from './utils';
import { FileUploadType, ReimbursementType } from './upload-types';
import { IQueryParamKeys } from '../types';
import {
    trackUploadStarted,
    trackUploadCompleted,
    trackReimbursementSubmitted
} from '../analytics';

export interface IUploadFormControls {
    fileInput: HTMLInputElement | null;
}

export abstract class SimpleFileUpload {
    private static readonly FileTooLargeErrorId: string = 'fileTooLargeError';
    private static readonly InvalidFileTypeErrorId: string =
        'invalidFileTypeError';
    private static readonly NetworkErrorId: string = 'networkError';
    private static readonly ValidationErrorId: string = 'validationError';
    private static readonly UnknownErrorId: string = 'unknownError';
    private static readonly UploadSucceededDialogId: string =
        'uploadSuccessDialog';
    private static readonly UploadFailedDialogId: string =
        'uploadFailureDialog';
    private static readonly UploadProgressContainerId: string =
        'uploadProgressContainer';
    private static readonly UploadProgressId: string = 'uploadProgress';

    protected readonly queryParamKeys!: IQueryParamKeys;

    readonly uploadInfo!: IUploadInfo;

    protected abstract getControls(): IUploadFormControls;

    protected constructor() {
        // Get query param keys from the JSON script element
        const queryParamKeysElement: HTMLScriptElement | null =
            document.getElementById('queryParamKeys') as HTMLScriptElement;

        if (!queryParamKeysElement?.textContent) {
            throw new Error('Query parameter keys configuration not found');
        }
        this.queryParamKeys = JSON.parse(
            queryParamKeysElement.textContent
        ) as IQueryParamKeys;

        // Get upload info from URL query parameters
        const reimbursementType: ReimbursementType = (getQueryParam(
            this.queryParamKeys.reimbursementType
        ) ?? '') as ReimbursementType;
        const uploadType: FileUploadType = (getQueryParam(
            this.queryParamKeys.uploadFileType
        ) ?? '') as FileUploadType;

        if (reimbursementType && uploadType) {
            this.uploadInfo = {
                reimbursementType,
                uploadType
            };
        }
        this.initializeEventListeners();
    }

    private showErrorDialog(): void {
        // Show the upload failed dialog (which is a ManagedDialogHandler)
        try {
            const dialogHandler = (window as any)[
                `${SimpleFileUpload.UploadFailedDialogId}-openDialog`
            ];

            if (dialogHandler && typeof dialogHandler.show === 'function') {
                dialogHandler.show();
            } else if (
                dialogHandler &&
                typeof dialogHandler.showModal === 'function'
            ) {
                // Fallback for HTMLDialogElement
                dialogHandler.showModal();
            } else {
                console.error('Error dialog handler not found or invalid');
            }
        } catch (error) {
            console.error('Failed to show error dialog:', error);
        }
    }

    /**
     * Maps HTTP status codes to their corresponding error element IDs and manages visibility
     * @param statusCode - The HTTP error status code from file upload
     */
    private showErrorForStatusCode(statusCode: number): void {
        // Define the mapping between HTTP status codes and error element IDs
        const statusCodeToErrorIdMap: Record<number, string> = {
            400: SimpleFileUpload.ValidationErrorId, // Bad Request (validation errors)
            403: SimpleFileUpload.NetworkErrorId, // Forbidden (auth/permission errors)
            404: SimpleFileUpload.NetworkErrorId, // Not Found (bucket/object not found)
            413: SimpleFileUpload.FileTooLargeErrorId, // Payload Too Large (file size exceeded)
            415: SimpleFileUpload.InvalidFileTypeErrorId, // Unsupported Media Type
            422: SimpleFileUpload.ValidationErrorId, // Unprocessable Entity (validation errors)
            429: SimpleFileUpload.NetworkErrorId, // Too Many Requests (rate limiting)
            500: SimpleFileUpload.NetworkErrorId, // Internal Server Error
            502: SimpleFileUpload.NetworkErrorId, // Bad Gateway
            503: SimpleFileUpload.NetworkErrorId, // Service Unavailable
            504: SimpleFileUpload.NetworkErrorId // Gateway Timeout
        };

        // Get all error element IDs and the target error ID for the status code
        const allErrorIds: string[] = [
            SimpleFileUpload.FileTooLargeErrorId,
            SimpleFileUpload.InvalidFileTypeErrorId,
            SimpleFileUpload.NetworkErrorId,
            SimpleFileUpload.ValidationErrorId,
            SimpleFileUpload.UnknownErrorId
        ];
        const targetErrorId: string =
            statusCodeToErrorIdMap[statusCode] ??
            SimpleFileUpload.UnknownErrorId;

        // Hide all error elements, except show the one matching the status code
        allErrorIds.forEach((errorId: string): void => {
            const element: HTMLElement | null =
                document.getElementById(errorId);
            if (element) {
                element.hidden = errorId !== targetErrorId;
            }
        });
    }

    private addLeaseIdToSuccessDialog(fileID: string): void {
        // Get the actual dialog element from the DOM, not the handler object
        const dialogElement: HTMLElement | null = document.getElementById(
            SimpleFileUpload.UploadSucceededDialogId
        );

        if (!dialogElement) {
            console.error(
                `[Upload] Cannot find dialog element: ${SimpleFileUpload.UploadSucceededDialogId}`
            );
            return;
        }

        const ctaLinkButton: HTMLLinkElement | null =
            dialogElement.querySelector('a.mcp-button');

        if (!ctaLinkButton) {
            console.error(`[Upload] Cannot find CTA button in success dialog`);
            return;
        }

        const currentHrefUrl: URL = new URL(ctaLinkButton.href);
        currentHrefUrl.searchParams.set(this.queryParamKeys.leaseDocId, fileID);
        ctaLinkButton.setAttribute('href', currentHrefUrl.href);
        ctaLinkButton.setAttribute('hx-get', currentHrefUrl.href);
        (window as any)['htmx'].process(ctaLinkButton);

        console.log(`[Upload] Added lease ID to success dialog: ${fileID}`);
    }

    private showSuccessDialog(): void {
        // Show the upload succeeded dialog (which is a ManagedDialogHandler)
        const dialogPropName: string = `${SimpleFileUpload.UploadSucceededDialogId}-openDialog`;
        const dialogId: string = SimpleFileUpload.UploadSucceededDialogId;

        console.log(
            `[Upload] Attempting to show success dialog: ${dialogPropName}`
        );

        const dialogHandler = (window as any)[dialogPropName];

        // Log comprehensive diagnostic information
        console.log(`[Upload] Dialog system state:`, {
            dialogPropName,
            dialogId,
            handlerExists: !!dialogHandler,
            handlerType: typeof dialogHandler,
            hasShowMethod:
                dialogHandler && typeof dialogHandler.show === 'function',
            hasShowModalMethod:
                dialogHandler && typeof dialogHandler.showModal === 'function',
            dialogElementExists: !!document.getElementById(dialogId),
            allDialogProps: Object.keys(window).filter(
                (k: string) => k.includes('Dialog') || k.includes('openDialog')
            )
        });

        // First, try the proper ManagedDialogHandler
        if (dialogHandler && typeof dialogHandler.show === 'function') {
            console.log(`[Upload] ✓ Calling show() on ManagedDialogHandler`);
            dialogHandler.show();
            return;
        }

        // THIS IS A DEVELOPMENT ERROR - Dialog system is not properly initialized
        // The dialog should have been initialized by dialogInit() when the page loaded

        console.error(
            '╔═══════════════════════════════════════════════════════════════╗\n' +
                '║ DIALOG SYSTEM INITIALIZATION ERROR                            ║\n' +
                '╠═══════════════════════════════════════════════════════════════╣\n' +
                '║ The success dialog handler is not available.                  ║\n' +
                '║ This indicates a problem with dialog initialization.          ║\n' +
                '║                                                               ║\n' +
                '║ Expected: window["' +
                dialogPropName +
                '"]                    ║\n' +
                '║ Got: ' +
                typeof dialogHandler +
                '                             ║\n' +
                '║                                                               ║\n' +
                '║ This should NEVER happen in production.                       ║\n' +
                '║ Check:                                                        ║\n' +
                '║  1. Dialog template is rendered on the page                   ║\n' +
                '║  2. dialogInit() was called for: ' +
                dialogId +
                '             ║\n' +
                '║  3. No JS errors during page load                             ║\n' +
                '║  4. HTMX page swaps are preserving dialog state               ║\n' +
                '╚═══════════════════════════════════════════════════════════════╝'
        );

        // Throw a descriptive error that will bubble up and show the error dialog
        throw new Error(
            `Dialog system failure: Success dialog handler "${dialogPropName}" is not initialized. ` +
                `Dialog element exists: ${!!document.getElementById(dialogId)}. ` +
                `This is a development error that must be fixed before deployment.`
        );
    }

    protected clearFileInput(): void {
        const { fileInput } = this.getControls();
        if (fileInput) {
            fileInput.value = '';
        }
    }

    private showProgress(): void {
        const progressContainer = document.getElementById(
            SimpleFileUpload.UploadProgressContainerId
        );
        if (progressContainer) {
            progressContainer.hidden = false;
        }
    }

    private hideProgress(): void {
        const progressContainer = document.getElementById(
            SimpleFileUpload.UploadProgressContainerId
        );
        if (progressContainer) {
            progressContainer.hidden = true;
        }
    }

    private updateProgress(progress: number): void {
        const progressElement = document.getElementById(
            SimpleFileUpload.UploadProgressId
        ) as HTMLProgressElement;
        if (progressElement) {
            progressElement.value = progress;
        }
    }

    protected hasUploadFile(fileInput: HTMLInputElement | null): boolean {
        return Boolean(fileInput?.files?.length);
    }

    protected initializeEventListeners(): void {
        const { fileInput } = this.getControls();

        if (fileInput) {
            fileInput.addEventListener('change', async () => {
                await this.updateFileSelection();
            });
            fileInput.addEventListener('click', (event: MouseEvent) => {
                event.stopPropagation();
            });
        }
    }

    protected async updateFileSelection(): Promise<void> {
        const { fileInput } = this.getControls();
        const hasUploadFile: boolean = this.hasUploadFile(fileInput);

        // For payment uploads, automatically proceed when file is selected
        if (hasUploadFile && fileInput) {
            const uploadFile: File | null = fileInput.files?.[0] || null;

            await this.uploadFile(uploadFile);
        }
    }

    protected async uploadFile(fileToUpload: File | null): Promise<void> {
        console.log('[Upload] Starting file upload:', fileToUpload?.name);

        if (fileToUpload) {
            // Track upload started for analytics
            if (
                this.uploadInfo?.reimbursementType &&
                this.uploadInfo?.uploadType
            ) {
                trackUploadStarted(
                    this.uploadInfo.reimbursementType,
                    this.uploadInfo.uploadType
                );
            }

            const fileUploadUtility: FileUploadUtility =
                new FileUploadUtility();

            if (!fileUploadUtility) {
                console.error('[Upload] Failed to create FileUploadUtility');
                this.showErrorForStatusCode(500);
                this.showErrorDialog();
                return;
            }

            try {
                console.log('[Upload] Getting signed URL...');
                const signedURLResponse: ISignedURLResponse =
                    await fileUploadUtility.getSignedURL(fileToUpload);
                console.log('[Upload] Got signed URL:', signedURLResponse);

                if (signedURLResponse) {
                    try {
                        // Show progress bar and reset to 0
                        this.showProgress();
                        this.updateProgress(0);

                        console.log('[Upload] Uploading file to signed URL...');
                        await fileUploadUtility.uploadFileToSignedURL(
                            fileToUpload,
                            signedURLResponse.url,
                            signedURLResponse.maxSize,
                            (progress: number) => {
                                this.updateProgress(progress);
                            }
                        );

                        // Complete progress and show success
                        console.log('[Upload] File uploaded successfully!');
                        this.updateProgress(100);

                        // Track upload completion for analytics
                        if (
                            this.uploadInfo?.reimbursementType &&
                            this.uploadInfo?.uploadType
                        ) {
                            trackUploadCompleted(
                                this.uploadInfo.reimbursementType,
                                this.uploadInfo.uploadType
                            );

                            // If this is a proof-of-payment upload, the reimbursement is fully submitted
                            if (
                                this.uploadInfo.uploadType ===
                                FileUploadType.ProofOfPayment
                            ) {
                                trackReimbursementSubmitted(
                                    this.uploadInfo.reimbursementType
                                );
                            }
                        }

                        if (
                            this.uploadInfo.uploadType === FileUploadType.Lease
                        ) {
                            console.log(
                                '[Upload] Adding lease ID to success dialog'
                            );
                            this.addLeaseIdToSuccessDialog(
                                signedURLResponse.fileID
                            );
                        }

                        console.log('[Upload] Showing success dialog...');
                        this.showSuccessDialog();
                    } catch (exception) {
                        console.error(
                            '[Upload] Upload failed with exception:',
                            exception
                        );
                        this.showErrorForStatusCode(
                            +(exception as Error)?.message
                        );
                        this.showErrorDialog();
                    } finally {
                        // Hide progress bar and clear file input
                        this.hideProgress();
                        this.clearFileInput();
                    }
                }
            } catch (exception) {
                console.error('[Upload] Failed to get signed URL:', exception);
                this.showErrorForStatusCode(+(exception as Error)?.message);
                this.showErrorDialog();
                this.hideProgress();
            } finally {
                this.clearFileInput();
            }
        }
    }

    selectFile(): void {
        const { fileInput } = this.getControls();

        if (fileInput) {
            fileInput.click();
        }
    }
}
